import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Home, Loader2, Sparkles } from "lucide-react";
import Logo from "@/components/Logo";

export default function Login() {
  const { login, formatApiError } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome home.");
      nav("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-hearth-cream">
      <div className="hidden lg:flex w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1600210492493-0946911123ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxjb3p5JTIwbW9kZXJuJTIwbGl2aW5nJTIwcm9vbSUyMHN1bmxpZ2h0fGVufDB8fHx8MTc4MjcyODgxMnww&ixlib=rb-4.1.0&q=85"
          alt="warm cozy home interior"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-hearth-ink/40 via-transparent to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h1 className="font-display text-5xl xl:text-6xl font-semibold leading-none tracking-tight">
            Your home,<br/>quietly organized.
          </h1>
          <p className="mt-5 max-w-md text-white/90 leading-relaxed">
            HearthHouse keeps your groceries, repairs, warranties, plants, insurance and bills
            in one warm place — shared with the people you live with.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md fade-up">
          <Link to="/" className="inline-block mb-10">
            <Logo size={40} showWord />
          </Link>

          <p className="caption text-hearth-mute mb-3">Sign in</p>
          <h2 className="font-display text-4xl font-semibold tracking-tight text-hearth-ink">
            Welcome back.
          </h2>
          <p className="text-hearth-mute mt-2">
            Your household, kept in warm order.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-5">
            <div>
              <Label className="caption text-hearth-mute" htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-warm mt-2 h-12"
                placeholder="you@home.com"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="caption text-hearth-mute" htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-link"
                  className="caption text-hearth-clayDark hover:text-hearth-clay"
                >
                  Forgot?
                </Link>
              </div>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-warm mt-2 h-12"
                placeholder="••••••••"
              />
            </div>
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-hearth-terracotta hover:bg-hearth-terracottaDark text-white font-medium transition-transform hover:-translate-y-0.5 active:scale-[0.98]"
            >
              {loading ? <Loader2 className="animate-spin" size={18}/> : "Sign in"}
            </Button>
          </form>

          <p className="mt-8 text-sm text-hearth-mute">
            New here?{" "}
            <Link to="/signup" data-testid="login-signup-link" className="text-hearth-terracotta font-medium underline-offset-4 hover:underline">
              Create your household
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
